import type { AnalyzerFinding, MatchMetrics } from '../diagnostics/analyzer';
import type { BattleEvent, BattleResult, CardId, PlayerId } from '../engine/types';

export const ANALYTICS_SCHEMA_VERSION = 1 as const;

export interface AnalyticsIdentity {
  anonymousId: string;
  sessionId: string;
  deletionToken: string;
}

export type SessionMode = 'menu' | 'gameplay' | 'paused';

export interface SessionSnapshot {
  schemaVersion: typeof ANALYTICS_SCHEMA_VERSION;
  anonymousId: string;
  sessionId: string;
  startedAt: string;
  occurredAt: string;
  activeSeconds: number;
  gameplaySeconds: number;
  menuSeconds: number;
  pausedSeconds: number;
  matchesStarted: number;
  matchesCompleted: number;
  matchesAbandoned: number;
  engineVersion: string;
  contentVersion: string;
}

export type AnalyticsEventName =
  | 'app_loaded'
  | 'deck_saved'
  | 'match_start_clicked'
  | 'match_started'
  | 'match_completed'
  | 'match_abandoned'
  | 'replay_started'
  | 'diagnostics_exported'
  | 'client_error';

export interface AnalyticsEventPayload {
  schemaVersion: typeof ANALYTICS_SCHEMA_VERSION;
  eventId: string;
  anonymousId: string;
  sessionId: string;
  name: AnalyticsEventName;
  occurredAt: string;
  properties: Readonly<Record<string, string | number | boolean | null>>;
}

export interface CardMatchStats {
  playerId: PlayerId;
  cardId: CardId;
  plays: number;
  rejected: number;
}

export interface CompletedMatchAnalytics {
  schemaVersion: typeof ANALYTICS_SCHEMA_VERSION;
  matchId: string;
  anonymousId: string;
  sessionId: string;
  startedAt: string;
  completedAt: string;
  arenaId: string;
  rulesetId: string;
  engineVersion: string;
  contentVersion: string;
  botId: string | null;
  botVersion: string | null;
  durationTicks: number;
  tickRate: number;
  result: BattleResult;
  commandsAccepted: number;
  commandsRejected: number;
  phases: readonly string[];
  cardStats: readonly CardMatchStats[];
  diagnosticMetrics: MatchMetrics;
  diagnosticCounts: { critical: number; errors: number; warnings: number };
  topFindings: readonly Pick<AnalyzerFinding, 'ruleId' | 'severity'>[];
  checksumVerified: boolean;
}

interface MutableCardStats {
  playerId: PlayerId;
  cardId: CardId;
  plays: number;
  rejected: number;
}

export class MatchAnalyticsAggregator {
  private readonly cardStats = new Map<string, MutableCardStats>();
  private readonly phases = new Set<string>();
  private accepted = 0;
  private rejected = 0;

  consume(events: readonly BattleEvent[]): void {
    for (const event of events) {
      if (event.type === 'command-accepted') this.accepted += 1;
      if (event.type === 'command-rejected') {
        this.rejected += 1;
        const stats = this.statsFor(event.command.playerId, event.command.cardId);
        stats.rejected += 1;
      }
      if (event.type === 'card-played') this.statsFor(event.playerId, event.cardId).plays += 1;
      if (event.type === 'phase-changed') this.phases.add(event.phase);
    }
  }

  snapshot(): { commandsAccepted: number; commandsRejected: number; phases: string[]; cardStats: CardMatchStats[] } {
    return {
      commandsAccepted: this.accepted,
      commandsRejected: this.rejected,
      phases: [...this.phases],
      cardStats: [...this.cardStats.values()]
        .sort((left, right) => left.playerId.localeCompare(right.playerId) || left.cardId.localeCompare(right.cardId))
        .map((item) => ({ ...item })),
    };
  }

  private statsFor(playerId: PlayerId, cardId: CardId): MutableCardStats {
    const key = `${playerId}:${cardId}`;
    const existing = this.cardStats.get(key);
    if (existing) return existing;
    const created: MutableCardStats = { playerId, cardId, plays: 0, rejected: 0 };
    this.cardStats.set(key, created);
    return created;
  }
}
